'use client'

import { Button } from '@/components/ui/button'
import { BodyText } from '@/components/ui/typography'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { useHistory } from '@/hooks/useHistory'
import { useSearch } from '@/hooks/useSearch'
import { cn } from '@/lib/utils'
import type { SearchResult } from '@/types/dictionary'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Keyboard from 'react-simple-keyboard'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { SearchBar } from './search-bar'
import { SearchResultItem } from './search-result-item'
import { useSettings } from '@/hooks/useSettings'
import { useGuestSearchLimit } from '@/hooks/useGuestSearchLimit'
import { SearchLimitBanner } from './search-limit-banner'
import { analytics } from '@/lib/analytics'
import radicalsData from '@/data/radicals.json'
import { HandwritingSheet } from './handwriting-sheet'
import 'react-simple-keyboard/build/css/index.css'

const ZHUYIN_KEYBOARD_LAYOUT = {
  default: [
    'ㄅ ㄉ ˇ ˋ ㄓ ˊ ˙ ㄚ ㄞ ㄢ ㄦ',
    'ㄆ ㄊ ㄍ ㄐ ㄔ ㄗ ㄧ ㄛ ㄟ ㄣ',
    'ㄇ ㄋ ㄎ ㄑ ㄕ ㄘ ㄨ ㄜ ㄠ ㄤ',
    'ㄈ ㄌ ㄏ ㄒ ㄖ ㄙ ㄩ ㄝ ㄡ ㄥ',
    '{space} ˉ {bksp}'
  ]
}

const ZHUYIN_KEYBOARD_DISPLAY = {
  '{bksp}': '⌫',
  '{space}': 'Space'
}

type RadicalPickerItem = {
  number: number
  glyph: string
  english?: string
  pinyin?: string | string[]
  strokes?: number
  variants?: string[]
}

const RADICAL_BUCKETS = [
  { id: '1-4', label: '1–4', min: 1, max: 4 },
  { id: '5-8', label: '5–8', min: 5, max: 8 },
  { id: '9-12', label: '9–12', min: 9, max: 12 },
  { id: '13+', label: '13+', min: 13, max: 99 }
] as const

interface SearchContainerProps {
  isOfflineMode?: boolean
  initialQuery?: string
  initialEnglishMode?: boolean
  onSearch?: (query: string) => void
  onEnglishModeChange?: (englishMode: boolean) => void
  onSelectEntry?: (entry: SearchResult) => void
  className?: string
  getResultHref?: (result: SearchResult) => string
  resultsContainerClassName?: string
  hideZhuyinButton?: boolean
  initialRadical?: number | null
  onRadicalChange?: (radical: number | null) => void
}

export function SearchContainer({
  isOfflineMode: controlledOfflineMode,
  initialQuery = '',
  initialEnglishMode = false,
  initialRadical = null,
  onSearch,
  onEnglishModeChange,
  onRadicalChange,
  onSelectEntry,
  className,
  getResultHref,
  resultsContainerClassName,
  hideZhuyinButton
}: SearchContainerProps) {
  const [input, setInput] = useState(initialQuery)
  // submittedQuery is what actually triggers the search (on Enter/click)
  const [submittedQuery, setSubmittedQuery] = useState(initialQuery)
  const [englishMode, setEnglishMode] = useState(initialEnglishMode)
  const [activeTab, setActiveTab] = useState<'results' | 'history'>('results')
  const [isZhuyinKeyboardOpen, setIsZhuyinKeyboardOpen] = useState(false)
  const [radical, setRadical] = useState<number | null>(initialRadical ?? null)
  const [radicalBucket, setRadicalBucket] =
    useState<(typeof RADICAL_BUCKETS)[number]['id']>('1-4')
  const [radicalQuery, setRadicalQuery] = useState('')
  const [isRadicalOpen, setIsRadicalOpen] = useState(false)
  const [isHandwritingOpen, setIsHandwritingOpen] = useState(false)

  const radicalsList = useMemo(
    () =>
      Object.values(radicalsData as Record<string, RadicalPickerItem>).map(
        (r) => r as RadicalPickerItem
      ),
    []
  )

  const selectedRadical =
    radical !== null
      ? radicalsList.find((item) => item.number === radical)
      : undefined

  const filteredRadicals = useMemo(() => {
    const bucket = RADICAL_BUCKETS.find((b) => b.id === radicalBucket)
    const q = radicalQuery.trim().toLowerCase()

    return radicalsList.filter((r) => {
      const inBucket =
        bucket && r.strokes
          ? r.strokes >= bucket.min && r.strokes <= bucket.max
          : true
      const pinyinText = Array.isArray(r.pinyin)
        ? r.pinyin.join(' ')
        : (r.pinyin ?? '')
      const variantsText = (r.variants || []).join(' ')
      const matchesQuery =
        !q ||
        r.glyph.includes(q) ||
        r.number.toString().includes(q) ||
        (r.english ?? '').toLowerCase().includes(q) ||
        pinyinText.toLowerCase().includes(q) ||
        variantsText.includes(q)
      return inBucket && matchesQuery
    })
  }, [radicalBucket, radicalQuery, radicalsList])

  const isOfflineMode = controlledOfflineMode ?? false
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const { settings } = useSettings()
  const guestLimit = useGuestSearchLimit(isOfflineMode)

  // Track what we last sent to onSearch to avoid syncing back our own updates
  const prevSentQuery = useRef(initialQuery)
  // Track what we last sent to onEnglishModeChange to avoid syncing back our own updates
  const prevSentEnglishMode = useRef(initialEnglishMode)
  const prevSentRadical = useRef<number | null>(initialRadical ?? null)

  // Handle explicit search trigger (Enter key or click)
  // Optional value param allows overriding current input state (e.g. for debounced search)
  const handleSearchSubmit = useCallback(
    (value?: string) => {
      const queryToCheck = typeof value === 'string' ? value : input
      const trimmedInput = queryToCheck.trim()

      // Don't search if guest is rate limited
      if (guestLimit.isGuest && guestLimit.isLimited) {
        return
      }

      // Increment counter for guests when submitting a non-empty search
      if (trimmedInput.length > 0 && guestLimit.isGuest) {
        guestLimit.incrementCount()
      }

      setSubmittedQuery(trimmedInput)

      // Track search event
      if (trimmedInput.length > 0) {
        analytics.searchPerformed(
          trimmedInput,
          englishMode ? 'english' : 'chinese'
        )
      }

      // Also notify parent if callback provided
      if (onSearch && trimmedInput !== prevSentQuery.current) {
        onSearch(trimmedInput)
        prevSentQuery.current = trimmedInput
      }

      // Switch to results tab
      setActiveTab('results')
    },
    [input, guestLimit, onSearch, englishMode]
  )

  // Trigger search automatically in offline mode on every input change
  useEffect(() => {
    if (isOfflineMode && input.trim() !== submittedQuery) {
      const id = window.setTimeout(() => handleSearchSubmit(input), 0)
      return () => window.clearTimeout(id)
    }
  }, [input, isOfflineMode, submittedQuery, handleSearchSubmit])

  // Listen to initialQuery (URL) and update input only if URL changed externally
  // (e.g., back button) - simple check: only sync if URL differs from what we sent
  useEffect(() => {
    // If initialQuery is different from what we last sent, it's an external change
    // (back button, direct URL change, etc.) - sync the input
    if (initialQuery !== prevSentQuery.current) {
      const id = window.setTimeout(() => {
        setInput(initialQuery)
        setSubmittedQuery(initialQuery)
        prevSentQuery.current = initialQuery
      }, 0)
      return () => window.clearTimeout(id)
    }
  }, [initialQuery]) // Only depend on initialQuery - don't re-run when input changes!

  // Listen to initialEnglishMode (URL) and update englishMode only if URL changed externally
  useEffect(() => {
    // If initialEnglishMode is different from what we last sent, it's an external change
    if (initialEnglishMode !== prevSentEnglishMode.current) {
      const id = window.setTimeout(() => {
        setEnglishMode(initialEnglishMode)
        prevSentEnglishMode.current = initialEnglishMode
      }, 0)
      return () => window.clearTimeout(id)
    }
  }, [initialEnglishMode])

  useEffect(() => {
    const incoming = initialRadical ?? null
    if (incoming !== prevSentRadical.current) {
      const id = window.setTimeout(() => {
        setRadical(incoming)
        prevSentRadical.current = incoming
      }, 0)
      return () => window.clearTimeout(id)
    }
  }, [initialRadical])

  // Use submittedQuery for actual search, and disable when rate limited
  const searchEnabled = !guestLimit.isLimited
  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    error,
    hasNextPage,
    fetchNextPage
  } = useSearch(
    searchEnabled ? submittedQuery : '', // Pass empty string when limited to prevent requests
    englishMode,
    isOfflineMode,
    radical
  )

  // Flatten pages array into a single array of results
  const results = data?.pages.flat() ?? []

  const { history, addToHistory, clearHistory } = useHistory()

  const handleResultClick = (item: SearchResult, index: number) => {
    addToHistory(item)
    analytics.searchResultClicked(item.id, index)
    onSelectEntry?.(item)
  }

  const hasRadicalFilter = radical !== null

  const showEmpty =
    !isLoading &&
    !isFetching &&
    submittedQuery.length > 0 &&
    results.length === 0

  const showZhuyinButton =
    !hideZhuyinButton &&
    (settings.pronunciationDisplay === 'zhuyin' ||
      settings.pronunciationDisplay === 'both')

  useEffect(() => {
    if (!showZhuyinButton && isZhuyinKeyboardOpen) {
      const id = window.setTimeout(() => setIsZhuyinKeyboardOpen(false), 0)
      return () => window.clearTimeout(id)
    }
  }, [showZhuyinButton, isZhuyinKeyboardOpen])

  const handleZhuyinKeyPress = (button: string) => {
    setInput((prev) => {
      if (button === '{bksp}') {
        return prev.slice(0, -1)
      }
      if (button === '{space}') {
        return `${prev} `
      }
      return `${prev}${button}`
    })
    searchInputRef.current?.focus()
  }

  const handleSelectRadical = (num: number) => {
    setRadical(num)
    setIsRadicalOpen(false)
    onRadicalChange?.(num)
    prevSentRadical.current = num
  }

  const handleClearRadical = () => {
    setRadical(null)
    setRadicalQuery('')
    onRadicalChange?.(null)
    prevSentRadical.current = null
  }

  const radicalControl = (
    <Popover open={isRadicalOpen} onOpenChange={setIsRadicalOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={hasRadicalFilter ? 'default' : 'outline'}
          className="rounded-lg px-3 py-2 font-normal"
        >
          {hasRadicalFilter && selectedRadical
            ? `${selectedRadical.glyph}`
            : '部'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-3" align="end" sideOffset={8}>
        <div className="flex items-center gap-2">
          <Input
            value={radicalQuery}
            onChange={(e) => setRadicalQuery(e.target.value)}
            placeholder="Filter glyph, #, pinyin, English"
            className="h-9"
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={handleClearRadical}
            disabled={!hasRadicalFilter}
          >
            Clear
          </Button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {RADICAL_BUCKETS.map((bucket) => (
            <Button
              key={bucket.id}
              type="button"
              variant={bucket.id === radicalBucket ? 'default' : 'secondary'}
              size="sm"
              onClick={() => setRadicalBucket(bucket.id)}
              className="flex-1 px-3 py-1"
            >
              {bucket.label}
            </Button>
          ))}
        </div>

        <div className="mt-3 grid max-h-64 grid-cols-4 gap-2 overflow-y-auto">
          {filteredRadicals.map((rad) => (
            <Button
              key={rad.number}
              type="button"
              variant={radical === rad.number ? 'default' : 'secondary'}
              className="relative flex h-20 flex-col items-start gap-0.5 overflow-hidden p-2"
              onClick={() => handleSelectRadical(rad.number)}
            >
              <span className="text-muted-foreground absolute top-1 right-2 text-xs">
                #{rad.number}
              </span>
              <span className="text-foreground text-lg">{rad.glyph}</span>
              {rad.english && (
                <span className="text-muted-foreground line-clamp-1 text-xs">
                  {rad.english}
                </span>
              )}
            </Button>
          ))}
          {filteredRadicals.length === 0 && (
            <BodyText className="text-muted-foreground col-span-4 py-6 text-center text-sm">
              No radicals match your filter.
            </BodyText>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )

  // Infinite scroll: intersection observer for loading more results
  const loadMoreRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observerElement = loadMoreRef.current
    if (!observerElement || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(observerElement)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <section
      className={cn(
        'bg-card text-card-foreground flex min-h-80 flex-col overflow-hidden rounded-xl border shadow-sm',
        className
      )}
    >
      <SearchBar
        value={input}
        onChange={setInput}
        onSubmit={handleSearchSubmit}
        englishMode={englishMode}
        onToggleMode={() => {
          const newMode = !englishMode
          setEnglishMode(newMode)
          if (onEnglishModeChange) {
            onEnglishModeChange(newMode)
            prevSentEnglishMode.current = newMode
          }
        }}
        isLoading={isLoading || isFetching}
        showZhuyinButton={showZhuyinButton}
        isZhuyinKeyboardOpen={isZhuyinKeyboardOpen}
        onToggleZhuyinKeyboard={() => setIsZhuyinKeyboardOpen((prev) => !prev)}
        inputRef={searchInputRef}
        radicalControl={radicalControl}
        onOpenHandwriting={() => setIsHandwritingOpen(true)}
      />

      {showZhuyinButton && isZhuyinKeyboardOpen && (
        <div className="zhuyin-keyboard bg-card rounded-lg border p-2 px-4 pb-2">
          <Keyboard
            layout={ZHUYIN_KEYBOARD_LAYOUT}
            display={ZHUYIN_KEYBOARD_DISPLAY}
            onKeyPress={handleZhuyinKeyPress}
            theme="hg-theme-default"
            buttonTheme={[
              {
                buttons: '{space}',
                class: 'zhuyin-button-space'
              },
              {
                buttons: '˙ ˊ ˇ ˋ',
                class: 'zhuyin-button-tone'
              }
            ]}
          />
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as 'results' | 'history')}
        className="flex min-h-0 flex-1 flex-col px-4 pt-1 pb-4"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="results">Results</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent
          value="results"
          className="flex min-h-0 flex-1 flex-col px-0 pt-4"
        >
          {/* Guest search limit banner */}
          <SearchLimitBanner
            className="mx-2 mb-2"
            isOfflineMode={isOfflineMode}
          />

          {submittedQuery.length === 0 &&
            !isLoading &&
            !error &&
            results.length === 0 &&
            !guestLimit.isLimited && (
              <BodyText className="text-muted-foreground px-6 py-10 text-center text-sm">
                Start searching to see results
              </BodyText>
            )}

          {isLoading && submittedQuery.length > 0 && (
            <BodyText className="text-muted-foreground px-6 py-10 text-center text-sm not-first:mt-0">
              Searching dictionary…
            </BodyText>
          )}

          {error && !guestLimit.isLimited && (
            <BodyText className="text-destructive px-6 py-10 text-center text-sm not-first:mt-0">
              Error loading results. Please try again.
            </BodyText>
          )}

          {showEmpty && (
            <BodyText className="text-muted-foreground px-6 py-10 text-center text-sm not-first:mt-0">
              {submittedQuery.length > 0
                ? `No results found for "${submittedQuery}".`
                : 'No results found for this radical.'}
            </BodyText>
          )}

          {!isLoading &&
            !error &&
            results.length > 0 &&
            !guestLimit.isLimited && (
              <div
                className={cn(
                  'divide-border min-h-0 flex-1 divide-y overflow-y-auto pr-1',
                  resultsContainerClassName
                )}
              >
                {results.map((item, i) => (
                  <SearchResultItem
                    key={i}
                    item={item}
                    onClick={() => handleResultClick(item, i)}
                    href={getResultHref?.(item)}
                  />
                ))}
                <div ref={loadMoreRef} className="h-4" />
                {isFetchingNextPage && (
                  <BodyText className="text-muted-foreground px-6 py-4 text-center text-sm">
                    Loading more results…
                  </BodyText>
                )}
              </div>
            )}
        </TabsContent>

        <TabsContent
          value="history"
          className="flex min-h-0 flex-1 flex-col overflow-y-auto px-0 pt-4"
        >
          {history.length === 0 ? (
            <BodyText className="text-muted-foreground px-6 py-10 text-center text-sm">
              Search history will show up here once you start exploring.
            </BodyText>
          ) : (
            <div className="space-y-4">
              <div className="text-muted-foreground flex items-center justify-between text-xs tracking-wide uppercase">
                <span>Recent Searches</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={clearHistory}
                >
                  Clear
                </Button>
              </div>
              <div className="divide-border divide-y">
                {history.map((item, i) => (
                  <SearchResultItem
                    key={i}
                    item={item}
                    onClick={() => handleResultClick(item, i)}
                  />
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Handwriting input sheet */}
      <HandwritingSheet
        open={isHandwritingOpen}
        onOpenChange={setIsHandwritingOpen}
        onSelectCharacter={(char) => {
          setInput((prev) => prev + char)
          searchInputRef.current?.focus()
        }}
      />
    </section>
  )
}
